/** The only gate control; room tiles deliberately carry none. */
import { useSessionKey } from "@auth/sessionStore";
import { nodesByRoom } from "@capture/captureNodeStatus";
import { useCaptureNodes } from "@capture/captureQueries";
import {
  RoomAudioGateControl,
  RoomAudioGateReadout,
} from "@capture/RoomAudioGateControl";
import { useRoomsList } from "@rooms/roomsQueries";
import {
  EmptyNote,
  LabelTape,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
} from "@scribenote/brian-design-system";

export const RoomAudioGateList = () => {
  const sessionKey = useSessionKey();
  // No polling on rooms: each room's desired revision rides the polled nodes.
  const { data: rooms } = useRoomsList(sessionKey, true);
  const { data: nodes } = useCaptureNodes(sessionKey);

  if (rooms.length === 0)
    return <EmptyNote>No rooms set up in this clinic yet.</EmptyNote>;

  const byRoom = nodesByRoom(nodes);
  const sorted = [...rooms].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <LedgerTable>
      <LedgerHead>
        <tr>
          <LedgerHeadCell>Room</LedgerHeadCell>
          <LedgerHeadCell>Gate</LedgerHeadCell>
          <LedgerHeadCell>Reading</LedgerHeadCell>
        </tr>
      </LedgerHead>
      <LedgerBody>
        {sorted.map((room) => {
          const roomNodes = byRoom.get(room.id) ?? [];
          return (
            <LedgerRow
              key={room.id}
              data-testid={`room-audio-gate-row-${room.id}`}
            >
              <LedgerCell>
                <LabelTape className="max-w-[12rem] truncate align-middle">
                  {room.name}
                </LabelTape>
              </LedgerCell>
              <LedgerCell>
                <RoomAudioGateControl
                  roomUuid={room.id}
                  roomName={room.name}
                  nodes={roomNodes}
                  desiredMode={room.audioGateMode}
                  desiredMarginDb={room.audioGateMarginDb}
                  desiredRevision={room.audioGateRevision}
                />
              </LedgerCell>
              <LedgerCell className="text-sm">
                <RoomAudioGateReadout roomUuid={room.id} nodes={roomNodes} />
              </LedgerCell>
            </LedgerRow>
          );
        })}
      </LedgerBody>
    </LedgerTable>
  );
};
